/** announcement-bar.js: publishes the bar's height and how much of it is still on screen (--announcement-bar-height / --announcement-bar-offset), which the fixed header rests on while the bar scrolls away; on close it removes the bar and records it in sessionStorage, so it stays closed on reload and across pages until the tab is closed — the pre-paint check lives inline in the section. */
(function () {
  // The section can be rendered several times (theme editor reload): a single delegated listener covers every bar, present and future.
  if (window.announcementBar) return;
  window.announcementBar = true;

  let observer = null;
  let barEl = null;
  let barHeight = 0;
  let offset = null;

  // The per-frame offset is written on the two subtrees that read it, never on <html>: changing any custom property on the root element rebuilds the inherited custom-property map for the whole document, whether or not anything uses it. Measured on the home page (1957 nodes) it costs ~6ms a frame against ~0.5ms on a ~110-node subtree, which is what made the header stutter while the bar scrolled away.
  let offsetTargets = [];

  function collectOffsetTargets() {
    offsetTargets = [document.querySelector(".header_wrap"), document.querySelector("mobile-menu")].filter(Boolean);
  }

  function writeOffset(value) {
    for (const el of offsetTargets) {
      if (value === null) el.style.removeProperty("--announcement-bar-offset");
      else el.style.setProperty("--announcement-bar-offset", `${value}px`);
    }
  }

  /** The CSS default assumes a single line; a wrapped message on a narrow screen is taller and would sit under the header. */
  function publishHeight(bar) {
    barEl = bar;
    barHeight = Math.round(bar.getBoundingClientRect().height);
    document.documentElement.style.setProperty("--announcement-bar-height", `${barHeight}px`);
    publishOffset();
  }

  /** How much of the bar is still on screen: the fixed header rests on it, then pins to the top once it has scrolled away. Read off the bar itself rather than derived from window.scrollY, which any overlay that locks the body (mobile menu, drawers) resets to 0 while the page visually stays put — the header would then drop back down by the height of a bar that is no longer on screen. */
  function publishOffset() {
    if (!barEl) return;
    const next = Math.max(0, Math.round(barEl.getBoundingClientRect().bottom));
    if (next === offset) return;
    offset = next;
    writeOffset(next);
  }

  // Published straight from the handler: `scroll` already fires at most once per frame, and deferring to rAF left the header one frame behind the page.
  function onScroll() {
    publishOffset();
  }

  function watch() {
    const bar = document.querySelector("[data-announcement-bar]");
    if (!bar) return;

    collectOffsetTargets();
    publishHeight(bar);
    window.addEventListener("scroll", onScroll, { passive: true });

    if (!("ResizeObserver" in window)) return;
    observer?.disconnect();
    observer = new ResizeObserver(() => publishHeight(bar));
    observer.observe(bar);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", watch, { once: true });
  } else {
    watch();
  }

  // Theme editor: the section is re-rendered on every setting change.
  document.addEventListener("shopify:section:load", watch);

  // A link in a message closes the search panel, which would otherwise stay open over the destination.
  document.addEventListener("click", (event) => {
    if (!event.target.closest("[data-announcement-bar] a[href]")) return;
    document.querySelectorAll("predictive-search-component").forEach((search) => search.close?.());
  });

  document.addEventListener("click", (event) => {
    const button = event.target.closest("[data-announcement-close]");
    if (!button) return;

    const bar = button.closest("[data-announcement-bar]");
    if (!bar) return;

    // Destroyed before the node goes: Swiper's autoplay timer would otherwise keep ticking on a detached element.
    bar.querySelector("slide-show")?.swiper?.destroy(true, true);
    observer?.disconnect();
    observer = null;
    window.removeEventListener("scroll", onScroll);
    barEl = null;
    offset = null;
    bar.remove();
    // Cleared on the targets too: an inline value left behind would keep offsetting the header once the bar is gone.
    writeOffset(null);
    offsetTargets = [];
    document.documentElement.style.removeProperty("--announcement-bar-height");
    // The header slides back to the top: overlays pinned under it (predictive search) have to re-measure.
    document.dispatchEvent(new CustomEvent("announcement-bar:close"));

    const key = bar.dataset.storageKey;
    if (!key) return;
    try {
      sessionStorage.setItem(key, "closed");
    } catch (e) {
      // Private browsing or storage blocked: the bar still closes, it just comes back on the next page.
    }
  });
})();
